import React, {useEffect, useState} from "react";
import {useParams, useNavigate} from 'react-router-dom';
import toast from 'react-hot-toast';
import Icon from "../components/ds/Icon";
import PostStructure from "../components/PostStructure";
import PublishingOptions from "../components/PublishingOptions";
import {postService} from '../container';
import {postState} from "../constants";
import {useGlobalState} from '../state';

export const getBodyFromPost = (post) => {
  const body = post.contents.find(content => content.type === 'body');

  return body ? body.content.text : '';
};

const getImageFromPost = (post) => {
  const image = post.contents.find(content => content.type === 'image');
  return image ? {
    url: image.content.final_path || image.content.temporal_key,
    description: image.content.description,
    final_path: image.content.final_path,
    slug: image.content.slug,
    temporal_key: image.content.temporal_key,
    image_format: image.content.image_format,
    low_quality_path: image.content.low_quality_path,
    mid_quality_path: image.content.mid_quality_path,
    high_quality_path: image.content.high_quality_path
  } : {};
};

export default function NewPost() {
  const {id} = useParams();
  const {user, media} = useGlobalState();
  const [post, setPost] = useState({
    isNew: true,
    isTouched: false,
    state: postState.draft,
    title: '',
    subtitle: '',
    seo_title: '',
    redirect_title: '',
    body: '',
    image: {},
    category: null,
    tags: [],
    video: '',
    target: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const navigate = useNavigate();

  const loadPost = async () => {
    if (!id) {
      return;
    }

    setLoading(true);

    try {
      const post = await postService.getById(id);

      setPost({
        isNeW: false,
        state: post.state,
        title: post.title,
        subtitle: post.subtitle,
        seo_title: post.seo_title,
        redirect_title: post.redirect_title,
        body: getBodyFromPost(post),
        image: getImageFromPost(post),
        category: post.category,
        tags: post.tags,
        video: post.video,
        target: post.target,
        published_at: post.published_at,
        updated_at: post.updated_at,
        last_published_at: post.last_published_at,
      });
    } catch (err) {
      toast.error('No se pudo cargar la publicación.');
      console.error(err);
    }

    setLoading(false);
  };

  const reSyncPost = async (post) => {
    setPublishing(true);
    try {
      await postService.resync(id);

      toast.success('Los cambios fueron sincronizados.');

      // return navigate(`/`);
      loadPost();
    } catch (err) {
      toast.error('Ocurrió un error inesperado al publicar la nota.');
      console.error(err);
    }

    setPublishing(false);
  };


  const updatePost = (post) => {
    setPost((p) => ({
      ...p,
      ...post,
      isTouched: true,
    }));
  };

  const updatePublishedPost = async (post) => {
    setSaving(true);

    const contents = [
      {
        type: 'body',
        content: {
          text: post.body,
        },
      },
    ];

    if (post.image?.url) {
      contents.push({
        type: 'image',
        content: {
          url: post.image.temporal_key,
          description: post.image.description,
          final_path: post.image.final_path,
          slug: post.image.slug,
          temporal_key: post.image.temporal_key,
          image_format: post.image.image_format,
          low_quality_path: post.image.low_quality_path,
          mid_quality_path: post.image.mid_quality_path,
          high_quality_path: post.image.high_quality_path
        },
      });
    }
    try {
        await postService.update(id, {
          category_id: post.category.id,
          title: post.title,
          seo_title: post.seo_title,
          redirect_title: post.redirect_title,
          subtitle: post.subtitle,
          contents,
          tags_id: post.tags.map(tag => tag.id),
          video: post.video,
          target: post.target,
        });

        toast.success('Se guardó la publicación.');
    } catch (err) {
      toast.error('Ocurrió un error inesperado al guardar la publicación.');
      console.error(err);
    }

    setSaving(false);
    loadPost()
  };

  const savePost = async (post) => {
    setSaving(true);

    const contents = [
      {
        type: 'body',
        content: {
          text: post.body,
        },
      },
    ];

    if (post.image?.url) {
      contents.push({
        type: 'image',
        content: {
          url: post.image.url || post.image.temporal_key,
          description: post.image.description,
          final_path: post.image.final_path,
          slug: post.image.slug,
          temporal_key:  post.image.temporal_key || post.image.url,
          image_format: post.image.image_format,
          low_quality_path: post.image.low_quality_path,
          mid_quality_path: post.image.mid_quality_path,
          high_quality_path: post.image.high_quality_path
        },
      });
    }

    try {
      if (post.isNew) {
        const {id} = await postService.create({
          media_id: media.id,
          author_id: user.id,
          category_id: post.category.id,
          title: post.title,
          subtitle: post.subtitle,
          seo_title: post.seo_title,
          redirect_title: post.redirect_title,
          language: 'es',
          contents,
          tags_id: post.tags.map(tag => tag.id),
          video: post.video,
          target: post.target,
        });

        setSaving(false);

        toast.success('Se creó la publicación.');

        return navigate(`/edit-post/${id}`);
      } else {
        await postService.update(id, {
          category_id: post.category.id,
          title: post.title,
          seo_title: post.seo_title,
          redirect_title: post.redirect_title,
          subtitle: post.subtitle,
          contents,
          tags_id: post.tags.map(tag => tag.id),
          video: post.video,
          target: post.target,
        });

        setPost((p) => ({
          ...p,
          isTouched: false,
        }));

        toast.success('Se guardó la publicación.');
      }
    } catch (err) {
      toast.error('Ocurrió un error inesperado al guardar la publicación.');
      console.error(err);
    }

    setSaving(false);
  };

  const publishPost = async (_, publishedAt) => {
    setPublishing(true);

    try {
      await postService.publish(id, {
        publisher_id: user.id,
        published_at: publishedAt?.toISOString(),
      });

      toast.success('Se publicó la nota.');

      // return navigate(`/`);
      loadPost();
    } catch (err) {
      toast.error('Ocurrió un error inesperado al publicar la nota.');
      console.error(err);
    }

    setPublishing(false);
  };

  const unpublishPost = async () => {
    setPublishing(true);

    try {
      await postService.unpublish(id);

      toast.success('Se despublicó la nota.');

      // return navigate(`/`);
      loadPost();
    } catch (err) {
      toast.error('Ocurrió un error inesperado al despublicar la nota.');
      console.error(err);
    }

    setPublishing(false);
  };

  const schedulePost = async (_, scheduledAt) => {
    if (!scheduledAt) {
      toast.error('Debes seleccionar una fecha y hora para programar la publicación.');
      return;
    }

    setPublishing(true);

    try {
      await postService.schedule(id, {
        publisher_id: user.id,
        published_at: scheduledAt.toISOString(),
      });

      toast.success('Se programó la nota.');

      // return navigate(`/`);
      loadPost();
    } catch (err) {
      toast.error('Ocurrió un error inesperado al programar la nota.');
      console.error(err);
    }

    setPublishing(false);
  };

  const unschedulePost = async () => {
    setPublishing(true);

    try {
      await postService.unschedule(id);

      toast.success('Se desprogramó la nota.');

      // return navigate(`/`);
      loadPost();
    } catch (err) {
      toast.error('Ocurrió un error inesperado al desprogramar la nota.');
      console.error(err);
    }

    setPublishing(false);
  }

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    loadPost();
  }, [id]);


  if (loading) {
    return <span>Loading...</span>;
  }

  return (
    <>
      <h1 className="t-heading-xl-cms">
        {post.isNew ? 'Crear post' : 'Editar post'}

        {!post.isNew && (
          <a className="t-action" href="#" target="_blank">Ver vista previa<Icon icon="out"/></a>
        )}
      </h1>

      <div id="content">
        <form id="post">
          <PostStructure post={post} onChange={updatePost}/>

          <PublishingOptions
            post={post}
            saving={saving}
            publishing={publishing}
            onChange={updatePost}
            onSave={savePost}
            onPublish={publishPost}
            onUnpublish={unpublishPost}
            onSchedule={schedulePost}
            onUnschedule={unschedulePost}
            onResync={reSyncPost}
            onUpdatedPublishedPost={updatePublishedPost}
          />
        </form>
      </div>
    </>
  )
}
